// ── Athena Cognitive Core ─────────────────────────────────────────────────────
// Central profile manager shared by Foco Total, Athena 2.0, and Área Inicial.
// Reads/writes the same localStorage key used by FocusTotalLanding.
// Profiles are isolated — cognitive data from one profile never leaks to another.

const PROFILES_KEY = 'athena_focus_profiles';
const ACTIVE_KEY   = 'athena_active_profile_id';

// ── Profile storage ───────────────────────────────────────────────────────────
export function loadProfiles() {
  try { const r = localStorage.getItem(PROFILES_KEY); return r ? JSON.parse(r) : []; }
  catch { return []; }
}

export function saveProfile(profile) {
  try {
    const all = loadProfiles();
    const idx = all.findIndex(p => p.id === profile.id);
    if (idx >= 0) all[idx] = profile; else all.push(profile);
    localStorage.setItem(PROFILES_KEY, JSON.stringify(all));
  } catch {}
}

export function getActiveProfileId() {
  try {
    const stored = localStorage.getItem(ACTIVE_KEY);
    if (stored) {
      const all = loadProfiles();
      if (all.some(p => p.id === stored)) return stored;
    }
    // Default: Vitor if exists, else first profile
    const all = loadProfiles();
    const vitor = all.find(p => p.name?.toLowerCase() === 'vitor');
    return vitor?.id || all[0]?.id || null;
  } catch { return null; }
}

export function setActiveProfile(id) {
  try { localStorage.setItem(ACTIVE_KEY, id); } catch {}
}

export function getActiveProfile() {
  const id = getActiveProfileId();
  if (!id) return null;
  return loadProfiles().find(p => p.id === id) || null;
}

export function updateProfileLastAccess(id) {
  try {
    const all = loadProfiles();
    const idx = all.findIndex(p => p.id === id);
    if (idx >= 0) {
      all[idx] = { ...all[idx], lastAccess: new Date().toISOString() };
      localStorage.setItem(PROFILES_KEY, JSON.stringify(all));
    }
  } catch {}
}

// ── Profile switch detection ──────────────────────────────────────────────────
// Recognises natural phrases like "não é o Vitor, é a Juliana" / "aqui é o João"
const SWITCH_PATTERNS = [
  /n[aã]o\s+[eé]\s+o\s+\w+\s*[,.]?\s+[eé]\s+[ao]\s+(\w+)/i,  // "não é o X, é a/o Y"
  /n[aã]o\s+sou\s+\w+[.,]?\s+sou\s+[ao]?\s*(\w+)/i,           // "não sou X, sou Y"
  /aqui\s+[eé]\s+[ao]?\s*(\w+)/i,                               // "aqui é a/o Y"
  /^sou\s+[ao]?\s*(\w+)\s*[.,!?]?$/i,                          // "sou a Juliana"
  /(?:meu\s+nome|me\s+chame(?:m)?)\s+[eé]\s+(\w+)/i,           // "meu nome é X"
  /pode\s+me\s+chamar\s+de\s+(\w+)/i,                           // "pode me chamar de X"
];

export function detectProfileSwitch(message) {
  const text = (message || '').trim();
  for (const re of SWITCH_PATTERNS) {
    const m = text.match(re);
    if (m) {
      const candidate = m[1];
      const profile = loadProfiles().find(p => p.name?.toLowerCase() === candidate.toLowerCase());
      if (profile) return { detected: true, profileId: profile.id, profileName: profile.name };
      return { detected: false }; // name not recognised — no switch
    }
  }
  return { detected: false };
}

// ── Cognitive context → short LLM directive ───────────────────────────────────
const STYLE_MAP = {
  VISUAL:         'Prefira blocos curtos, exemplos visuais e referencias concretas. Evite texto corrido.',
  ESTRUTURADO:    'Prefira passos numerados, titulos e progressao logica clara.',
  CONVERSACIONAL: 'Responda de forma dialogica, frases menores, perguntas guiadas quando util.',
  'PRÁTICO':      'Priorize acao, exemplos reais e aplicacao imediata. Reduza teoria.',
};

export function getCognitiveContext(profile) {
  if (!profile?.cognitive?.traits?.length) return '';
  const [primary, secondary] = profile.cognitive.traits;
  const style = STYLE_MAP[primary] || '';
  const secNote = secondary && secondary !== primary
    ? ` Tendencia secundaria: ${secondary.toLowerCase()}.`
    : '';
  return `[Perfil cognitivo de ${profile.name}: ${style}${secNote}]`;
}

// ── Layout config for Foco Total UI ──────────────────────────────────────────
export function getFocusLayoutProfile(cognitiveProfile) {
  if (!cognitiveProfile?.scores) return {
    layoutDensity: 'balanced', visualSupport: 'low', responseStyle: 'direct',
    showProgress: true, useCards: false, useImages: false, reduceAnimations: false,
  };

  const { scores, traits = [] } = cognitiveProfile;
  const [primary] = traits;
  const vis   = scores.VISUAL         || 0;
  const est   = scores.ESTRUTURADO    || 0;
  const conv  = scores.CONVERSACIONAL || 0;
  const prat  = scores['PRÁTICO']     || 0;

  return {
    layoutDensity:    est  >= 9 ? 'spacious' : conv >= 9 ? 'compact' : 'balanced',
    visualSupport:    vis  >= 9 ? 'high'     : vis  >= 6 ? 'medium'  : 'low',
    responseStyle:    primary === 'CONVERSACIONAL' ? 'guided'
                    : primary === 'PRÁTICO'        ? 'practical'
                    : primary === 'ESTRUTURADO'    ? 'structured'
                    : 'direct',
    showProgress:     est >= 6 || prat >= 6,
    useCards:         vis >= 6 || est  >= 6,
    useImages:        vis >= 9,
    reduceAnimations: conv >= 12,
  };
}
